/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React from 'react';
import styles from './Headline.module.scss';
import { Grid } from '../Grid';
import { Text, type TextProps } from '@az/starc-ui';
import { HeadlineUnderline } from '../HeadlineUnderline/HeadlineUnderline';
import cx from 'classnames';

export type Props = {
  id?: string;
  underline?: boolean;
  children?: string;
  className?: string;
  headlineClassName?: string;
  color?: TextProps['color'];
  emphasize?: 'all' | 'last';
  size?: TextProps['size'];
  as?: keyof React.JSX.IntrinsicElements;
};

export function Headline({
  id,
  underline = true,
  children,
  className,
  headlineClassName,
  color,
  emphasize,
  size,
  as = 'h2',
}: Props) {
  if (!children) {
    return null;
  }

  const headlineSize: NonNullable<TextProps['size']> = size ? size : '200';
  let title = children;
  let emphasized = '';

  if (emphasize === 'last') {
    const headlineParts = children.split(' ');
    emphasized = headlineParts.pop() ?? '';
    title = headlineParts.join(' ');
  } else if (emphasize === 'all') {
    emphasized = children;
    title = '';
  }

  return (
    <Grid data-testid="headline" container className={className} spacing={0}>
      <Grid item md={12} xs={12} lg={12}>
        <Text
          as={as}
          size={headlineSize}
          weight={emphasize === 'last' ? 'light' : 'medium'}
          color={color}
          textCase="uppercase"
          className={cx(styles.headline, headlineClassName)}
          attributes={{ id }}
        >
          {title}
          {emphasized && (
            <Text as="strong" weight="heavy" className={styles.emphasizedText}>
              {emphasized}
            </Text>
          )}
        </Text>
      </Grid>
      {underline && (
        <Grid item md={12} xs={12}>
          <HeadlineUnderline />
        </Grid>
      )}
    </Grid>
  );
}
